import React from 'react';
import { CalendarGrid } from './CalendarGrid';
import { MonthGrid } from './MonthGrid';
import { YearGrid } from './YearGrid';
import { ViewMode, DateRange } from '@/shared/types/datepicker';

interface CalendarContentProps {
  currentDate: Date;
  selectedDate?: Date | null;
  selectedRange?: DateRange;
  onDateSelect: (date: Date) => void;
  onDateHover?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  viewMode: ViewMode;
  weekDays?: string[];
  monthNames?: string[];
  startOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  showWeekNumbers?: boolean;
  yearRange?: number;
  hoverDate?: Date | null;
  onMonthSelect?: (monthIndex: number) => void;
  onYearSelect?: (year: number) => void;
  renderDay?: (props: {
    day: number;
    date: Date;
    isSelected: boolean;
    isInRange: boolean;
    isStart: boolean;
    isEnd: boolean;
    isFirstInRange?: boolean;
    isLastInRange?: boolean;
    afterSelected?: boolean;
    isDisabled: boolean;
    isToday: boolean;
  }) => React.ReactNode;
}

export const CalendarContent: React.FC<CalendarContentProps> = ({
  currentDate,
  selectedDate,
  selectedRange,
  onDateSelect,
  onDateHover,
  minDate,
  maxDate,
  viewMode,
  weekDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  startOfWeek = 1,
  showWeekNumbers = false,
  yearRange = 12,
  hoverDate,
  onMonthSelect,
  onYearSelect,
  renderDay,
}) => {
  const handleMonthSelect = (monthIndex: number) => {
    onMonthSelect?.(monthIndex);
  };

  const handleYearSelect = (year: number) => {
    onYearSelect?.(year);
  };

  const renderView = () => {
    switch (viewMode) {
      case 'days':
        return (
          <CalendarGrid
            currentDate={currentDate}
            selectedDate={selectedDate}
            selectedRange={selectedRange}
            onDateSelect={onDateSelect}
            onDateHover={onDateHover}
            minDate={minDate}
            maxDate={maxDate}
            weekDays={weekDays}
            startOfWeek={startOfWeek}
            showWeekNumbers={showWeekNumbers}
            hoverDate={hoverDate}
            renderDay={renderDay}
          />
        );
      case 'months':
        return (
          <MonthGrid
            currentDate={currentDate}
            selectedMonth={currentDate.getMonth()}
            onMonthSelect={handleMonthSelect}
            monthNames={monthNames}
          />
        );
      case 'years':
        return (
          <YearGrid
            currentDate={currentDate}
            selectedYear={currentDate.getFullYear()}
            onYearSelect={handleYearSelect}
            yearRange={yearRange}
          />
        );
      default:
        return null;
    }
  };

  return <div className="ui-flex ui-h-full ui-justify-center ui-p-3">{renderView()}</div>;
};
